import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { v, num, attachmentLabel, hasAttachmentRef, FieldRow, KeepTogether, Section, SectionNumberingProvider, SigningBlock, TemplateDocument, TemplateFooter, TemplateLetterhead, } from '../../../atoms/index.js';
import { expandPartyProjection } from '../../../parties/index.js';
export const metadata = {
    // Document sections in fixed order (per ADR-0049). Fields bind to these
    // refs via `field.section`, and `inputSections` groups the editor panels
    // so the form headers show computed § numbers.
    sections: [
        { ref: 'parter-och-fastighet', title: 'Parter och fastighet' },
        { ref: 'bakgrund', title: 'Bakgrund' },
        {
            ref: 'ordinarie-och-evakueringslagenhet',
            title: 'Ordinarie och evakueringslägenhet',
        },
        { ref: 'hyra-under-evakueringen', title: 'Hyra under evakueringen' },
        { ref: 'hyresgastens-ataganden', title: 'Hyresgästens åtaganden' },
        { ref: 'var-radd-om-evakueringslagenheten', title: 'Var rädd om evakueringslägenheten' },
        { ref: 'underskrift', title: 'Underskrifter' },
    ],
    templateKey: 'evakuering',
    version: 'v1',
    category: 'Ombyggnad och ändring',
    title: 'Överenskommelse om evakueringslägenhet vid renovering',
    shortTitle: 'Evakueringsavtal',
    titleSuffix: { key: 'apartment_official_number', prefix: 'lgh' },
    formNumber: 'Formulär R-09',
    preamble: 'Hyresvärden och hyresgästen kommer överens om att hyresgästen tillfälligt bor i en evakueringslägenhet under den tid som renoveringsarbetet av den ordinarie lägenheten pågår.',
    description: 'Tillfälligt tilläggsavtal mellan hyresvärd och hyresgäst som reglerar hyresgästens vistelse i en evakueringslägenhet under tiden som den ordinarie lägenheten renoveras.\n\n' +
        'Vem undertecknar: hyresgästen (en eller två) och hyresvärdens representant.\n' +
        'Leverans: skickas via Scrive.\n\n' +
        'Kopplad mall — skapas under det bostadshyresavtal som evakueringen gäller. Ett separat avtal om avstående av besittningsskydd avseende evakueringslägenheten ska bifogas som obligatorisk bilaga.',
    status: 'active',
    signingMode: 'scrive',
    connectedTo: ['bostadshyra'],
    // Same lease, same parties (ADR-0055). Roles line up 1:1 with the parent
    // (lessor0 = hyresvärd, lessee0/1 = hyresgäst), so by-role-ordinal is
    // correct. The `apartment_*` fields describe the PERMANENT (parent-lease)
    // apartment — the `evac_*` fields describe the evacuation apartment and
    // are NOT inherited. Both inherited scalars have extraction paths on the
    // parent, so they survive an imported parent (no IMPORT_TODO here).
    inheritFromParent: {
        parties: 'by-role-ordinal',
        property: true,
        values: [
            { key: 'apartment_official_number', fromParentKey: 'property_official_apt_no' },
            { key: 'apartment_monthly_rent', fromParentKey: 'rent_amount' },
        ],
    },
    signingPlacementAnchors: 'per-party-label',
    partySlots: [
        {
            id: 'lessor',
            label: 'Hyresvärd',
            signingLabel: 'Förvaltare',
            role: 'lessor',
            ordinal: 0,
            required: true,
            signs: true,
        },
        {
            id: 'lessee_primary',
            label: 'Hyresgäst (primär)',
            signingLabel: 'Hyresgäst 1',
            role: 'lessee',
            ordinal: 0,
            required: true,
            signs: true,
        },
        {
            id: 'lessee_co',
            label: 'Medhyresgäst',
            signingLabel: 'Eventuell hyresgäst 2',
            role: 'lessee',
            ordinal: 1,
            signs: true,
            help: 'Valfritt — lägg till om kontraktet har två hyresgäster.',
        },
    ],
    // Party projection (ADR-0065). Shared section; lessor name keeps its
    // "Hyresvärdens namn" label; hyresgäster carry "(hyresgäst 1/2)" suffixes.
    partyProjection: [
        {
            slot: 'lessor',
            section: 'parter-och-fastighet',
            idHint: 'org',
            fields: [
                {
                    attr: 'legal_name',
                    key: 'landlord_name',
                    label: 'Hyresvärdens namn',
                    requiredAtFinal: true,
                },
                { attr: 'id_number', key: 'landlord_org_number', requiredAtFinal: true },
            ],
        },
        {
            slot: 'lessee_primary',
            section: 'parter-och-fastighet',
            idHint: 'person',
            labelSuffix: ' (hyresgäst 1)',
            fields: [
                { attr: 'legal_name', key: 'tenant_name', requiredAtFinal: true },
                { attr: 'id_number', key: 'tenant_personnummer', help: 'PII — ej sökbart' },
            ],
        },
        {
            slot: 'lessee_co',
            section: 'parter-och-fastighet',
            idHint: 'person',
            labelSuffix: ' (hyresgäst 2)',
            fields: [
                { attr: 'legal_name', key: 'cotenant_name' },
                { attr: 'id_number', key: 'cotenant_personnummer', help: 'PII — ej sökbart' },
            ],
        },
    ],
    propertySlots: [
        {
            id: 'property',
            label: 'Fastighet (ordinarie lägenhet)',
            multi: false,
            required: true,
        },
    ],
    inputSections: [
        {
            id: 'parter',
            label: 'Parter och fastighet',
            sectionRefs: ['parter-och-fastighet'],
            copyDefault: false,
        },
        {
            id: 'lagenheter',
            label: 'Ordinarie och evakueringslägenhet',
            sectionRefs: ['ordinarie-och-evakueringslagenhet'],
        },
        {
            id: 'ataganden',
            label: 'Hyresgästens åtaganden',
            sectionRefs: ['hyresgastens-ataganden'],
        },
    ],
};
const ROOM_COUNT_OPTIONS = [
    { value: '1 rum och kokskåp', label: '1 rum och kokskåp', code: 'r1_kokskap', summary: '1 room with a kitchenette cupboard.' },
    { value: '1 rum och kokvrå', label: '1 rum och kokvrå', code: 'r1_kokvra', summary: '1 room with a kitchen nook.' },
    { value: '1 rum och kök', label: '1 rum och kök', code: 'r1_kok', summary: '1 room with a full kitchen.' },
    { value: '1,5 rum och kök', label: '1,5 rum och kök', code: 'r1_5_kok', summary: '1.5 rooms with a full kitchen.' },
    { value: '2 rum och kokvrå', label: '2 rum och kokvrå', code: 'r2_kokvra', summary: '2 rooms with a kitchen nook.' },
    { value: '2 rum och kök', label: '2 rum och kök', code: 'r2_kok', summary: '2 rooms with a full kitchen.' },
    { value: '2,5 rum och kök', label: '2,5 rum och kök', code: 'r2_5_kok', summary: '2.5 rooms with a full kitchen.' },
    { value: '3 rum och kök', label: '3 rum och kök', code: 'r3_kok', summary: '3 rooms with a full kitchen.' },
    { value: '3,5 rum och kök', label: '3,5 rum och kök', code: 'r3_5_kok', summary: '3.5 rooms with a full kitchen.' },
    { value: '4 rum och kök', label: '4 rum och kök', code: 'r4_kok', summary: '4 rooms with a full kitchen.' },
    { value: '4,5 rum och kök', label: '4,5 rum och kök', code: 'r4_5_kok', summary: '4.5 rooms with a full kitchen.' },
    { value: '5 rum och kök', label: '5 rum och kök', code: 'r5_kok', summary: '5 rooms with a full kitchen.' },
    { value: '6 rum och kök', label: '6 rum och kök', code: 'r6_kok', summary: '6 rooms with a full kitchen.' },
    { value: '7 rum och kök', label: '7 rum och kök', code: 'r7_kok', summary: '7 rooms with a full kitchen.' },
    { value: '8 rum och kök', label: '8 rum och kök', code: 'r8_kok', summary: '8 rooms with a full kitchen.' },
];
export const variableSchema = [
    // §1 Parter — generated from metadata.partyProjection (ADR-0065). All
    // three parties share the "parter-och-fastighet" section; the hyresgäster
    // are disambiguated by "(hyresgäst 1/2)" suffixes.
    ...expandPartyProjection(metadata),
    // §1 Parter och fastighet — Fastighet
    {
        key: 'property_designation',
        label: 'Fastighetsbeteckning',
        type: 'text',
        section: 'parter-och-fastighet',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'name' },
    },
    // §3 Ordinarie och evakueringslägenhet — Ordinarie lägenhet
    {
        key: 'apartment_official_number',
        label: 'Objektnummer ordinarie lägenhet',
        type: 'text',
        section: 'ordinarie-och-evakueringslagenhet',
        requiredAtFinal: true,
        // Per-unit identifier — never carried over by copy-from (ADR-0050).
        scopes: { copy: 'never' },
    },
    {
        key: 'apartment_address',
        label: 'Adress ordinarie lägenhet',
        type: 'textarea',
        section: 'ordinarie-och-evakueringslagenhet',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'address' },
    },
    {
        key: 'apartment_property',
        label: 'Fastighet ordinarie lägenhet',
        type: 'text',
        section: 'ordinarie-och-evakueringslagenhet',
        autofill: { kind: 'property', field: 'name' },
        help: 'Fastighetsbeteckning för den ordinarie lägenheten.',
    },
    {
        key: 'apartment_room_count',
        label: 'Antal rum ordinarie lägenhet',
        type: 'select',
        section: 'ordinarie-och-evakueringslagenhet',
        options: ROOM_COUNT_OPTIONS,
    },
    {
        key: 'apartment_monthly_rent',
        label: 'Månadshyra ordinarie lägenhet (SEK)',
        type: 'currency',
        min: 0,
        section: 'ordinarie-och-evakueringslagenhet',
        // Monetary amount — per-contract, opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
    },
    // §3 Ordinarie och evakueringslägenhet — Evakueringslägenhet
    {
        key: 'evac_object_number',
        label: 'Objektnummer evakueringslägenhet',
        type: 'text',
        section: 'ordinarie-och-evakueringslagenhet',
        requiredAtFinal: true,
        // Per-unit identifier — never carried over by copy-from (ADR-0050).
        scopes: { copy: 'never' },
    },
    {
        key: 'evac_address',
        label: 'Adress evakueringslägenhet',
        type: 'textarea',
        section: 'ordinarie-och-evakueringslagenhet',
        requiredAtFinal: true,
    },
    {
        key: 'evac_property',
        label: 'Fastighet evakueringslägenhet',
        type: 'text',
        section: 'ordinarie-och-evakueringslagenhet',
        help: 'Fastighetsbeteckning för evakueringsfastigheten (om den skiljer sig från ordinarie).',
    },
    {
        key: 'evac_room_count',
        label: 'Antal rum evakueringslägenhet',
        type: 'select',
        section: 'ordinarie-och-evakueringslagenhet',
        options: ROOM_COUNT_OPTIONS,
    },
    {
        key: 'evac_monthly_rent',
        label: 'Månadshyra evakueringslägenhet (SEK)',
        type: 'currency',
        min: 0,
        section: 'ordinarie-och-evakueringslagenhet',
        requiredAtFinal: true,
        // Monetary amount — per-contract, opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
    },
    // §5 Hyresgästens åtaganden — Återflytt
    {
        key: 'notice_period_months',
        label: 'Varseltid för återflytt (månader)',
        type: 'number',
        min: 0,
        integer: true,
        section: 'hyresgastens-ataganden',
        default: 1,
        help: 'Hyresvärden ska meddela tidpunkt för återflytt senast så här många månader före planerat återflytt.',
    },
    // §5 Hyresgästens åtaganden — obligatorisk bilaga (avstående)
    {
        key: 'possession_waiver_annex',
        label: 'Bilaga – avstående från besittningsskydd',
        type: 'attachment_ref',
        section: 'hyresgastens-ataganden',
        requiredAtFinal: true,
        attachmentDefaultName: 'Avstående från besittningsskydd – evakueringslägenhet',
        help: 'Obligatorisk bilaga: överenskommelsen om avstående av besittningsskydd för evakueringslägenheten. Välj eller ladda upp den.',
    },
];
export const sampleValues = {
    landlord_name: 'Bostadsägarna Stockholm AB',
    landlord_org_number: '556677-8899',
    property_designation: 'Klockaren 5',
    tenant_name: 'Anna Andersson',
    tenant_personnummer: '19850412-1234',
    cotenant_name: 'Bo Andersson',
    cotenant_personnummer: '19830605-2345',
    apartment_official_number: '1101',
    apartment_address: 'Götgatan 35\n116 21 Stockholm',
    apartment_property: 'Klockaren 5',
    apartment_room_count: '3 rum och kök',
    apartment_monthly_rent: 13200,
    evac_object_number: '7204',
    evac_address: 'Skånegatan 88\n116 37 Stockholm',
    evac_property: 'Murbruket 3',
    evac_room_count: '2 rum och kök',
    evac_monthly_rent: 11800,
    notice_period_months: 1,
};
export function Letterhead() {
    return _jsx(TemplateLetterhead, { title: metadata.title, formNumber: metadata.formNumber });
}
export function PageFooter() {
    return _jsx(TemplateFooter, { formNumber: metadata.formNumber });
}
export function Body({ mode, values, attachments, signatories, }) {
    return (_jsxs(SectionNumberingProvider, { sections: metadata.sections ?? [], values: values, mode: mode, children: [_jsx("p", { className: "mb-4 italic text-stone-600", children: metadata.preamble }), _jsxs(Section, { sectionRef: "parter-och-fastighet", title: "Parter och fastighet", children: [_jsx(FieldRow, { label: "Hyresv\u00E4rd", children: v(values.landlord_name) }), _jsx(FieldRow, { label: "Organisationsnummer", children: v(values.landlord_org_number) }), _jsx(FieldRow, { label: "Hyresg\u00E4st 1", children: v(values.tenant_name) }), _jsx(FieldRow, { label: "Personnummer (hyresg\u00E4st 1)", children: v(values.tenant_personnummer) }), _jsx(FieldRow, { label: "Hyresg\u00E4st 2", children: v(values.cotenant_name) }), _jsx(FieldRow, { label: "Personnummer (hyresg\u00E4st 2)", children: v(values.cotenant_personnummer) }), _jsx(FieldRow, { label: "Fastighet", children: v(values.property_designation) })] }), _jsx(Section, { sectionRef: "bakgrund", title: "Bakgrund", children: _jsx("p", { className: "text-[10.5px] leading-relaxed", children: "Hyresv\u00E4rden genomf\u00F6r renoveringsarbeten i den l\u00E4genhet som hyresg\u00E4sten/-erna (\u201DHyresg\u00E4sten\u201D) f\u00F6rhyr. Under tiden som hyresv\u00E4rden genomf\u00F6r dessa \u00E5tg\u00E4rder, kommer den l\u00E4genhet som hyresg\u00E4sten f\u00F6rhyr vara obeboelig, varf\u00F6r hyresv\u00E4rden och Hyresg\u00E4sten (\u201DParterna\u201D) kommer \u00F6verens om att Hyresg\u00E4sten tillf\u00E4lligt ska bo i en evakueringsl\u00E4genhet under tiden som arbetena p\u00E5g\u00E5r. Med anledning av detta har Parterna kommit \u00F6verens om detta evakueringsavtal, som utg\u00F6r ett tillf\u00E4lligt till\u00E4ggsavtal mellan hyresavtalsparterna." }) }), _jsxs(Section, { sectionRef: "ordinarie-och-evakueringslagenhet", title: "Ordinarie och evakueringsl\u00E4genhet", children: [_jsx("p", { className: "text-[10.5px] leading-relaxed", children: "Under den tid som den f\u00F6rhyrda l\u00E4genheten genomg\u00E5r renoveringsarbeten, f\u00E5r Hyresg\u00E4sten tillf\u00E4lligt ta i anspr\u00E5k f\u00F6ljande evakueringsl\u00E4genhet:" }), _jsxs("table", { className: "mt-2 w-full table-fixed border-collapse text-[10.5px] leading-relaxed", children: [_jsx("thead", { children: _jsxs("tr", { className: "border-b border-stone-400 text-left", children: [_jsx("th", { className: "py-1 pr-2 font-semibold" }), _jsx("th", { className: "py-1 pr-2 font-semibold", children: "Objektnummer" }), _jsx("th", { className: "py-1 pr-2 font-semibold", children: "Adress" }), _jsx("th", { className: "py-1 pr-2 font-semibold", children: "Fastighet" }), _jsx("th", { className: "py-1 pr-2 font-semibold", children: "Antal rum" }), _jsx("th", { className: "py-1 font-semibold", children: "M\u00E5nadshyra" })] }) }), _jsxs("tbody", { children: [_jsxs("tr", { className: "border-b border-stone-200 align-top", children: [_jsx("td", { className: "py-1 pr-2 font-semibold", children: "Ordinarie l\u00E4genhet" }), _jsx("td", { className: "py-1 pr-2", children: v(values.apartment_official_number) }), _jsx("td", { className: "py-1 pr-2 whitespace-pre-line", children: v(values.apartment_address) }), _jsx("td", { className: "py-1 pr-2", children: v(values.apartment_property) }), _jsx("td", { className: "py-1 pr-2", children: v(values.apartment_room_count) }), _jsxs("td", { className: "py-1", children: [num(values.apartment_monthly_rent), " kr"] })] }), _jsxs("tr", { className: "align-top", children: [_jsx("td", { className: "py-1 pr-2 font-semibold", children: "Evakueringsl\u00E4genhet" }), _jsx("td", { className: "py-1 pr-2", children: v(values.evac_object_number) }), _jsx("td", { className: "py-1 pr-2 whitespace-pre-line", children: v(values.evac_address) }), _jsx("td", { className: "py-1 pr-2", children: v(values.evac_property) }), _jsx("td", { className: "py-1 pr-2", children: v(values.evac_room_count) }), _jsxs("td", { className: "py-1", children: [num(values.evac_monthly_rent), " kr"] })] })] })] })] }), _jsxs(Section, { sectionRef: "hyra-under-evakueringen", title: "Hyra under evakueringen", children: [_jsx("p", { className: "text-[10.5px] leading-relaxed", children: "Hyresg\u00E4sten ska betala hyra f\u00F6r evakueringsl\u00E4genheten under den tid som hyresg\u00E4sten \u00E4r evakuerad. Om hyresg\u00E4sten har blivit tilldelad en l\u00E4genhet som har en h\u00F6gre hyra \u00E4n sin ordinarie hyra, ska Hyresg\u00E4sten endast betala den ordinarie hyran. Om Hyresg\u00E4sten i st\u00E4llet f\u00E5tt en l\u00E4genhet som har en l\u00E4gre hyra \u00E4n sin ordinarie hyra, ska Hyresg\u00E4sten betala den l\u00E4gre hyran. Detta kommer att korrigeras med ett hyresavdrag p\u00E5 evakueringsl\u00E4genhetens hyresavier." }), _jsx("p", { className: "mt-2 text-[10.5px] leading-relaxed", children: "Genom undertecknande av denna \u00F6verenskommelse godk\u00E4nner Hyresg\u00E4sten att l\u00E4genheten uthyrs i befintligt skick och att Hyresg\u00E4sten \u00E4r inf\u00F6rst\u00E5dd med att inga underh\u00E5lls\u00E5tg\u00E4rder g\u00F6rs inf\u00F6r och under tiden som Hyresg\u00E4sten \u00E4r evakuerad." })] }), _jsxs(Section, { sectionRef: "hyresgastens-ataganden", title: "Hyresg\u00E4stens \u00E5taganden", children: [_jsx("p", { className: "text-[10.5px] leading-relaxed", children: "Hyresg\u00E4sten f\u00F6rbinder sig att:" }), _jsxs("ul", { className: "mt-1 list-disc pl-5 text-[10.5px] leading-relaxed", children: [_jsxs("li", { children: ["Flytta tillbaka till sin ordinarie l\u00E4genhet n\u00E4r den \u00E4r klar f\u00F6r inflyttning, om inte parterna skriftligen kommer \u00F6verens om annat. Hyresv\u00E4rden ska meddela tidpunkt f\u00F6r \u00E5terflytt s\u00E5 snart som m\u00F6jligt, men senast", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: num(values.notice_period_months) }), ' ', "m\u00E5nad f\u00F6re planerat \u00E5terflytt."] }), _jsxs("li", { children: ["Ing\u00E5 avtal om avst\u00E5ende av besittningsskydd f\u00F6r evakueringsl\u00E4genheten (", hasAttachmentRef(values.possession_waiver_annex)
                                        ? attachmentLabel(attachments, values.possession_waiver_annex)
                                        : 'Bilaga', ")."] }), _jsx("li", { children: "L\u00E4mna evakueringsl\u00E4genheten v\u00E4l st\u00E4dad och \u00E5terl\u00E4mna tillh\u00F6rande nycklar till hyresv\u00E4rden senast kl. 12.00 den f\u00F6rsta vardagen efter aviserat \u00E5terflyttsdatum." })] })] }), _jsxs(Section, { sectionRef: "var-radd-om-evakueringslagenheten", title: "Var r\u00E4dd om evakueringsl\u00E4genheten", children: [_jsx("p", { className: "text-[10.5px] leading-relaxed", children: "Notera att Hyresg\u00E4sten \u00E4r skyldig att v\u00E4l v\u00E5rda den tillf\u00E4lliga l\u00E4genheten och \u00E4ven i \u00F6vrigt iaktta de skyldigheter som p\u00E5kommer en hyresg\u00E4st enligt lag och vid var tid g\u00E4llande ordningsregler f\u00F6r fastigheten." }), _jsx("p", { className: "mt-2 text-[10.5px] leading-relaxed", children: "L\u00E4genheten besiktigas innan du flyttar in och efter att du flyttat ut. Om n\u00E5got fattas eller har g\u00E5tt s\u00F6nder, f\u00F6rbeh\u00E5ller hyresv\u00E4rden sig r\u00E4tten att debitera Hyresg\u00E4sten f\u00F6r uppkommen skada / brist." }), _jsx("p", { className: "mt-2 text-[10.5px] leading-relaxed", children: "Hyresg\u00E4sten ansvarar f\u00F6r att iaktta folkbokf\u00F6ringslagens regler under evakueringstiden och att efters\u00E4nda post till evakueringsadressen." })] }), _jsx(Section, { sectionRef: "underskrift", title: "Underskrifter", children: _jsx(KeepTogether, { children: _jsx(SigningBlock, { statement: true, parties: [
                            { label: 'Hyresgäst 1', role: 'lessee', ordinal: 0 },
                            { label: 'Eventuell hyresgäst 2', role: 'lessee', ordinal: 1 },
                            { label: 'Hyresvärdens representant', role: 'lessor', ordinal: 0 },
                        ], signatories: signatories }) }) })] }));
}
export function Component(props) {
    return (_jsx(TemplateDocument, { header: _jsx(Letterhead, {}), footer: _jsx(PageFooter, {}), children: _jsx(Body, { ...props }) }));
}
//# sourceMappingURL=index.js.map